/**
 * dedup.js — Remove duplicate Point features from a GeoJSON FeatureCollection.
 *
 * Two points are considered duplicates when they share the same coordinate
 * within a given tolerance (default: exact match, i.e. 0 m).  For each group
 * of duplicates only the *first* occurrence is kept; the rest are discarded.
 *
 * Non-Point features (LineString, Polygon, …) are never touched.
 */

const EARTH_RADIUS_METERS = 6371008.8

function featureId(feature) {
  return String(feature?.id ?? feature?.properties?.id ?? "")
}

function exactPointKey(feature) {
  if (feature?.geometry?.type !== "Point") return ""
  const coordinates = feature.geometry.coordinates
  if (!Array.isArray(coordinates) || coordinates.length < 2) return ""
  const [lng, lat] = coordinates
  if (!Number.isFinite(Number(lng)) || !Number.isFinite(Number(lat))) return ""
  return `${lng},${lat}`
}

/**
 * Return the ids of every Point feature belonging to an exact-coordinate duplicate group.
 */
export function findDuplicatePointIds(fc) {
  const groups = new Map()

  for (const feature of fc?.features ?? []) {
    const key = exactPointKey(feature)
    const id = featureId(feature)
    if (!key || !id) continue

    const ids = groups.get(key) ?? []
    ids.push(id)
    groups.set(key, ids)
  }

  return new Set([...groups.values()].filter((ids) => ids.length > 1).flat())
}

/**
 * Haversine great-circle distance between two [lng, lat] coordinates (metres).
 */
export function haversineMeters([lng1, lat1], [lng2, lat2]) {
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLng = toRad(lng2 - lng1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.sqrt(a))
}

/**
 * Remove duplicate Point features from a FeatureCollection.
 *
 * @param {object} fc               - GeoJSON FeatureCollection
 * @param {number} toleranceMeters  - Max distance (m) to still consider
 *                                    two points identical. Default 0 = exact.
 * @returns {{ fc: object, removed: number }}
 *   fc      — new FeatureCollection with duplicates removed
 *   removed — count of removed features
 */
export function removeDuplicatePoints(fc, toleranceMeters = 0) {
  const features = fc?.features ?? []
  const kept = []
  const keptPoints = [] // parallel array of [lng, lat] for kept Point features

  for (const feature of features) {
    if (feature?.geometry?.type !== "Point") {
      // Non-point features are always kept unchanged
      kept.push(feature)
      continue
    }

    const [lng, lat] = feature.geometry.coordinates

    if (toleranceMeters === 0) {
      // Fast path: exact coordinate match (string key comparison)
      const key = `${lng},${lat}`
      if (keptPoints.some((p) => p._key === key)) continue
      keptPoints.push({ _key: key })
    } else {
      // Tolerance path: haversine distance check against all kept points
      const isDupe = keptPoints.some(
        (p) => haversineMeters([lng, lat], p._coord) <= toleranceMeters
      )
      if (isDupe) continue
      keptPoints.push({ _coord: [lng, lat] })
    }

    kept.push(feature)
  }

  return {
    fc: { ...fc, features: kept },
    removed: features.length - kept.length,
  }
}
